import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import "./teachers.css";

export default function TeacherList() {
  const navigate = useNavigate();
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));
  const schoolId = school?.school?.id;

  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  const getInitials = (name = "") =>
    name.split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "T";

  /* ---------- LOAD ---------- */
  const loadTeachers = async () => {
    try {
      setLoading(true);
      const res = await API.get("/school-admin/teachers/", {
        headers: { "X-SCHOOL-ID": schoolId },
      });
      setTeachers(res.data.teachers || []);
    } catch {
      alert("Failed to load teachers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTeachers(); }, []);

  /* ---------- ARCHIVE ---------- */
  const archiveTeacher = async (id) => {
    if (!window.confirm("Archive this teacher?\n\nThey can be restored later.")) return;
    try {
      await API.post(`/school-admin/teachers/${id}/archive/`, {}, {
        headers: { "X-SCHOOL-ID": schoolId },
      });
      setTeachers(prev => prev.filter(t => t.id !== id));
    } catch {
      alert("Failed to archive teacher");
    }
  };

  /* ---------- DELETE ---------- */
  const deleteTeacher = async (id) => {
    if (!window.confirm("Delete this teacher permanently?")) return;
    try {
      await API.delete(`/school-admin/teachers/delete/${id}/`, {
        headers: { "X-SCHOOL-ID": schoolId },
      });
      setTeachers(prev => prev.filter(t => t.id !== id));
    } catch {
      alert("Delete failed");
    }
  };

  return (
    <div className="teacher-container">

      {/* ══ HERO ══ */}
      <div className="teacher-hero">
        <div>
          <span className="teacher-hero-icon">👩‍🏫</span>
          <h2>Teachers</h2>
          <p>
            {loading
              ? "Loading…"
              : `${teachers.length} active teacher${teachers.length !== 1 ? "s" : ""} on staff`}
          </p>
        </div>
        <div className="header-buttons">
          <a href="/school-admin/teachers/archived" className="btn-primary">
            📦 Archived
          </a>
          <a href="/school-admin/teachers/add" className="btn-primary">
            + Add Teacher
          </a>
        </div>
      </div>

      {/* ══ TABLE ══ */}
      <div className="teacher-table-card">
        <table className="teacher-table">
          <thead>
            <tr>
              <th>Teacher</th>
              <th>Email / Phone</th>
              <th>Assignments</th>
              <th style={{ width: "130px" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4">
                  <div className="teacher-empty-state">
                    <span style={{ fontSize: 42 }}>⏳</span>
                    <strong>Loading teachers…</strong>
                  </div>
                </td>
              </tr>
            ) : teachers.length === 0 ? (
              <tr>
                <td colSpan="4">
                  <div className="teacher-empty-state">
                    <span style={{ fontSize: 48 }}>👩‍🏫</span>
                    <strong>No Teachers Found</strong>
                    <p>Add your first teacher to get started.</p>
                    <button onClick={() => navigate("/school-admin/teachers/add")}>
                      + Add Teacher
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              teachers.map(t => (
                <tr
                  key={t.id}
                  className="click-row"
                  onClick={() => navigate(`/school-admin/teachers/${t.id}`)}
                >
                  {/* AVATAR + NAME */}
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                      {t.photo
                        ? <img
                            src={`https://navith.cloud${t.photo}`}
                            className="teacher-photo"
                            alt={t.name}
                          />
                        : <div className="teacher-avatar">{getInitials(t.name)}</div>
                      }
                      <div>
                        <div style={{ fontWeight:700, color:"#1e293b", fontSize:"14px" }}>
                          {t.name}
                        </div>
                        {t.teacher_id && (
                          <div style={{ fontSize:"12px", color:"#94a3b8", marginTop:"2px" }}>
                            ID: {t.teacher_id}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* CONTACT */}
                  <td>
                    <div style={{ fontSize:"13px", color:"#334155" }}>{t.email || "—"}</div>
                    <div style={{ fontSize:"12px", color:"#64748b", marginTop:"3px" }}>{t.phone || "—"}</div>
                  </td>

                  {/* ASSIGNMENTS */}
                  <td>
                    <div className="assignment-tags">
                      {t.assignments.length === 0 ? (
                        <span className="tag" style={{ background:"#f1f5f9", color:"#94a3b8" }}>
                          No Assignments
                        </span>
                      ) : (
                        t.assignments.map((a, idx) => (
                          <span
                            key={idx}
                            className={`tag ${a.is_class_teacher ? "tag-primary" : ""}`}
                          >
                            {a.class}-{a.section} | {a.subject}
                            {a.is_class_teacher && " ⭐"}
                          </span>
                        ))
                      )}
                    </div>
                  </td>

                  {/* ACTIONS */}
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display:"flex", flexDirection:"column", gap:"7px" }}>
                      <button
                        className="btn-warning"
                        onClick={() => archiveTeacher(t.id)}
                      >
                        📦 Archive
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => deleteTeacher(t.id)}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
